import {
  Dialog,
  DialogActions,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect } from "react";
import { usePlan } from "../context/PlanContext";
import { Link } from "react-router-dom";
import "../Css/PlansDialog.css";

const PUBLIC_NAMES = new Set(["starter", "advance", "custom"]);

function formatCap(n) {
  if (n === -1) return "Unlimited";
  if (n == null || Number.isNaN(Number(n))) return "—";
  return String(n);
}

function historyLabel(p) {
  const days =
    typeof p.historyDays === "number"
      ? p.historyDays
      : typeof p.liveTrackingDays === "number"
        ? p.liveTrackingDays
        : 0;
  if (days === -1) return "Unlimited";
  if (days === 0) return "None";
  return `${days} days`;
}

function podLabel(p) {
  const days = typeof p.podDays === "number" ? p.podDays : 0;
  if (days === -1) return "Unlimited";
  if (days === 1) return "Today only";
  if (days === 0) return "None";
  if (days === 365) return "1 year";
  return `${days} days`;
}

function planPriceParts(p) {
  const name = String(p.planName || "").toLowerCase();
  if (p.isCustom || name === "custom") {
    return { main: "Custom", sub: "Pricing on request" };
  }
  if (name === "starter" || p.charges === "0") {
    return { main: "Free", sub: "Forever" };
  }
  if (p.trialDays > 0) {
    return {
      main: `₹${p.charges}`,
      sub: `/month · ${p.trialDays}-day free trial`,
    };
  }
  return { main: `₹${p.charges}`, sub: "/month" };
}

function planRows(p) {
  return [
    { label: "Drivers", value: formatCap(p.allowedDriver) },
    { label: "Packers", value: formatCap(p.allowedPacker) },
    { label: "Dispatchers", value: formatCap(p.allowedDispatcher) },
    { label: "Orders / month", value: formatCap(p.freeOrdersPerMonth) },
    {
      label: "Live tracking",
      value: p.liveTrackingEnabled ? "Yes" : "No",
    },
    { label: "History", value: historyLabel(p) },
    { label: "POD", value: podLabel(p) },
  ];
}

function catalogPlans(raw) {
  return [...(raw || [])]
    .filter((p) => {
      if (p.isListed === false) return false;
      const n = String(p.planName || "").toLowerCase();
      return PUBLIC_NAMES.has(n) || p.isCustom === true;
    })
    .sort((a, b) => {
      const rank = (p) => {
        const n = String(p.planName || "").toLowerCase();
        if (n === "starter") return 1;
        if (n === "advance") return 2;
        if (n === "custom" || p.isCustom) return 3;
        return 4;
      };
      return rank(a) - rank(b);
    });
}

function isCurrentPlan(plan, p) {
  if (!plan || !p) return false;
  const currentName = String(plan.planName || "").toLowerCase();
  const pName = String(p.planName || "").toLowerCase();
  const onCustom = plan.isCustom === true || currentName === "custom";

  if (onCustom) {
    return p.isCustom === true || pName === "custom";
  }

  if (plan.planNumber != null && p.planNumber != null) {
    return Number(p.planNumber) === Number(plan.planNumber);
  }

  return !!currentName && pName === currentName;
}

export default function PlansDialog({ open, onClose }) {
  const { billing, refreshBilling, openUpgradeModal, plan } = usePlan();

  useEffect(() => {
    if (open) refreshBilling();
  }, [open, refreshBilling]);

  const plans = catalogPlans(billing?.plans);
  const canUpgrade = plan && !plan.isCustom;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="plans-dialog-root"
      scroll="paper"
      PaperProps={{
        className: "plans-dialog-paper",
        sx: {
          overflow: "hidden",
          maxHeight: "none",
        },
      }}
    >
      <div className="plans-dialog-header">
        <div>
          <h2 className="plans-dialog-heading">Plans</h2>
          <p className="plans-dialog-subtitle">
            Current plan:{" "}
            <strong>{plan?.planName || "—"}</strong>
            {billing?.usageBlocked ? (
              <span className="plans-dialog-locked"> · Account locked</span>
            ) : null}
          </p>
        </div>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          className="plans-dialog-close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Plain div — avoid MUI DialogContent overflow-y: auto */}
      <div className="plans-dialog-body">
        {plans.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            Loading plans…
          </Typography>
        ) : (
          <div className="plans-dialog-grid">
            {plans.map((p) => {
              const current = isCurrentPlan(plan, p);
              const price = planPriceParts(p);
              return (
                <article
                  key={p._id || p.planNumber}
                  className={`plans-dialog-card ${current ? "current" : ""}`}
                >
                  <div className="plans-dialog-card-top">
                    <div className="plans-dialog-card-name-row">
                      <h3>{p.planName}</h3>
                      {current && (
                        <span className="plans-dialog-badge">Selected</span>
                      )}
                    </div>
                    <div className="plans-dialog-price">
                      <span className="plans-dialog-price-main">
                        {price.main}
                      </span>
                      <span className="plans-dialog-price-sub">
                        {price.sub}
                      </span>
                    </div>
                  </div>

                  <dl className="plans-dialog-features">
                    {planRows(p).map((row) => (
                      <div key={row.label} className="plans-dialog-feature">
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <DialogActions className="plans-dialog-actions">
        <Button
          component={Link}
          to="/Billing"
          onClick={onClose}
          className="plans-dialog-btn-secondary"
        >
          Billing & usage
        </Button>
        {canUpgrade ? (
          <Button
            variant="contained"
            disableElevation
            className="plans-dialog-btn-primary"
            onClick={() => {
              onClose?.();
              openUpgradeModal(billing);
            }}
          >
            Upgrade plan
          </Button>
        ) : (
          <span className="plans-dialog-footnote">
            On Custom — contact sales to change plans.
          </span>
        )}
      </DialogActions>
    </Dialog>
  );
}
